// Pure layered layout for one object's neighbourhood in a Salesforce org — no React, no DOM.
//
// The selected object sits in the centre. Objects it looks up to (its PARENTS in graph terms:
// Contact.AccountId → Account) are drawn in rings ABOVE it, one ring per hop; objects that look up
// to it (its CHILDREN: Case.ContactId → Contact) in rings BELOW. Node size follows the record
// count (square-root scale, so a 100× bigger object is 10× the area, not 100×). Everything is
// deterministic: every sort has a table-name tie-break, so the same input always yields the same
// picture and the tests can assert on coordinates.
//
// Input is the graph API's shapes verbatim (GRAPH_API.md): `center` is a GraphNode, `direct` /
// `indirect` / `edges` are the neighbours response, `nodesByKey` maps graphNodeKey → GraphNode so
// each neighbour (a bare TableRef on the wire) gets its record count and attributes.

import { graphNodeKey } from 'DataGraph/dataGraphApi';

export const LAYOUT_DEFAULTS = {
  rMin: 10,
  rMax: 28,
  rowGap: 110,
  slotGap: 14,
  marginX: 24,
  marginY: 36,
  minWidth: 360,
  maxPerLayer: 40,
  charW: 6.5,
  labelMax: 24,
};

const EMPTY = { width: 0, height: 0, nodes: [], edges: [], selfLoops: [], omitted: {}, maxRowCount: 0 };

const byName = (a, b) => String(a.table).localeCompare(String(b.table));

const truncate = (s, max) => {
  const t = String(s || '');
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
};

/** Visual node from a GraphNode (or a bare TableRef when the node list lacks it). */
function describe(node, key, o, maxRowCount) {
  const attrs = (node && node.attributes) || {};
  const rowCount = node && node.rowCount != null ? Number(node.rowCount) : null;
  let r = o.rMin;
  if (rowCount != null && maxRowCount > 0) {
    r = o.rMin + (o.rMax - o.rMin) * Math.sqrt(Math.max(0, rowCount) / maxRowCount);
    r = Math.max(o.rMin, Math.min(o.rMax, r));
  }
  const label = truncate(node.table, o.labelMax);
  return {
    key,
    table: node.table,
    schema: node.schema || null,
    label,
    fullLabel: attrs.label || null,
    rowCount,
    sizeBytes: node && node.sizeBytes != null ? Number(node.sizeBytes) : null,
    custom: attrs.custom === true,
    keyPrefix: attrs.keyPrefix || null,
    untracked: rowCount == null,
    r,
    slot: Math.max(2 * r + 8, label.length * o.charW) + o.slotGap,
    fields: [],
  };
}

export function layoutNetwork({ center, direct = [], indirect = [], edges = [], nodesByKey, hops = 1 }, opts = {}) {
  const o = { ...LAYOUT_DEFAULTS, ...opts };
  if (!center || !center.table) return EMPTY;
  const maxHops = Math.max(1, Math.min(6, Number(hops) || 1));
  const centerKey = graphNodeKey(center);
  const resolve = (ref) => (nodesByKey && nodesByKey.get(graphNodeKey(ref))) || { schema: ref.schema, table: ref.table, attributes: {} };

  // ---- 1. layer assignment ---------------------------------------------------------------
  // placed: key -> { node, layer, hops, parentVotes, childVotes }
  const placed = new Map();
  const selfEdges = [];
  const candidateEdges = new Map();          // edge id -> edge, everything that might be drawn
  const edgesById = new Map();
  (edges || []).forEach((e) => { if (e && e.id != null) edgesById.set(String(e.id), e); });

  (direct || []).forEach((d) => {
    const e = d && d.edge;
    if (!e || !e.child || !e.parent) return;
    const ck = graphNodeKey(e.child);
    const pk = graphNodeKey(e.parent);
    edgesById.set(String(e.id), e);
    if (ck === centerKey && pk === centerKey) { selfEdges.push(e); return; }
    const otherRef = d.node || (ck === centerKey ? e.parent : e.child);
    const k = graphNodeKey(otherRef);
    if (k === centerKey) return;
    let p = placed.get(k);
    if (!p) {
      p = { node: resolve(otherRef), hops: 1, parentVotes: 0, childVotes: 0 };
      placed.set(k, p);
    }
    if (d.role === 'CHILD') p.childVotes += 1; else p.parentVotes += 1;
    candidateEdges.set(String(e.id), e);
  });
  placed.forEach((p) => {
    // A table that is both a parent and a child of the centre (Account ⇄ Contact) is drawn once,
    // on the side with more edges; a tie goes above (the lookup the centre carries).
    p.layer = p.childVotes > p.parentVotes ? 1 : -1;
  });

  if (maxHops > 1) {
    const pathEdges = [];
    (indirect || []).forEach((i) => {
      if (!i || !i.node || !Array.isArray(i.path) || i.path.length === 0) return;
      if (i.hops > maxHops) return;
      // Every edge on the path is a candidate line (drawn below if both ends are placed), even
      // when the node itself is already on an inner ring — a cross-link between two placed
      // objects is worth showing.
      i.path.forEach((id) => { const e = edgesById.get(String(id)); if (e) pathEdges.push(e); });
      const k = graphNodeKey(i.node);
      if (k === centerKey || placed.has(k)) return;
      const first = edgesById.get(String(i.path[0]));
      if (!first) return;
      // The first hop leaves the centre either upwards (the centre is that edge's child, so it is
      // following a lookup to a parent) or downwards (something looks up to the centre).
      const side = graphNodeKey(first.child) === centerKey ? -1 : 1;
      placed.set(k, { node: resolve(i.node), hops: i.hops, layer: side * i.hops, parentVotes: 0, childVotes: 0 });
    });
    pathEdges.forEach((e) => {
      const ck = graphNodeKey(e.child);
      const pk = graphNodeKey(e.parent);
      const known = (k) => k === centerKey || placed.has(k);
      if (known(ck) && known(pk) && !(ck === centerKey && pk === centerKey)) candidateEdges.set(String(e.id), e);
    });
  }

  // ---- 2. overflow per layer ---------------------------------------------------------------
  const layers = new Map();                   // layer -> [key]
  placed.forEach((p, k) => {
    if (!layers.has(p.layer)) layers.set(p.layer, []);
    layers.get(p.layer).push(k);
  });
  const omitted = {};
  const overflowNodes = [];
  layers.forEach((keys, layer) => {
    if (keys.length <= o.maxPerLayer) return;
    const ranked = keys.slice().sort((a, b) => {
      const ra = placed.get(a).node.rowCount; const rb = placed.get(b).node.rowCount;
      if ((ra == null) !== (rb == null)) return ra == null ? 1 : -1;
      if (ra != null && rb != null && ra !== rb) return rb - ra;
      return byName(placed.get(a).node, placed.get(b).node);
    });
    const drop = ranked.slice(o.maxPerLayer);
    drop.forEach((k) => placed.delete(k));
    omitted[layer] = drop.length;
    layers.set(layer, ranked.slice(0, o.maxPerLayer));
    overflowNodes.push({ layer, count: drop.length });
  });
  // Edges to an omitted node are dropped with it.
  const drawnEdges = [];
  candidateEdges.forEach((e) => {
    const ck = graphNodeKey(e.child);
    const pk = graphNodeKey(e.parent);
    const known = (k) => k === centerKey || placed.has(k);
    if (known(ck) && known(pk)) drawnEdges.push(e);
  });

  // ---- 3. sizing ------------------------------------------------------------------------------
  let maxRowCount = center.rowCount != null ? Number(center.rowCount) : 0;
  placed.forEach((p) => { if (p.node.rowCount != null) maxRowCount = Math.max(maxRowCount, Number(p.node.rowCount)); });
  const visual = new Map();                   // key -> visual node
  const centerNode = describe(center, centerKey, o, maxRowCount);
  centerNode.layer = 0; centerNode.role = 'CENTER'; centerNode.hops = 0; centerNode.isCenter = true;
  visual.set(centerKey, centerNode);
  placed.forEach((p, k) => {
    const v = describe(p.node, k, o, maxRowCount);
    v.layer = p.layer; v.hops = p.hops; v.role = p.layer < 0 ? 'PARENT' : 'CHILD'; v.isCenter = false;
    visual.set(k, v);
  });
  overflowNodes.forEach(({ layer, count }) => {
    const key = `overflow:${layer}`;
    const label = `+${count} more`;
    visual.set(key, {
      key, table: label, schema: null, label, fullLabel: null, rowCount: null, sizeBytes: null, custom: false, keyPrefix: null,
      untracked: false, r: o.rMin, slot: Math.max(2 * o.rMin + 8, label.length * o.charW) + o.slotGap, fields: [],
      layer, hops: Math.abs(layer), role: layer < 0 ? 'PARENT' : 'CHILD', isCenter: false, overflow: true, overflowCount: count,
    });
    layers.get(layer).push(key);
  });

  // ---- 4. bundling ----------------------------------------------------------------------------
  const bundles = new Map();                  // `${child}=>${parent}` -> bundle
  drawnEdges.forEach((e) => {
    const from = graphNodeKey(e.child);
    const to = graphNodeKey(e.parent);
    const id = `${from}=>${to}`;
    let b = bundles.get(id);
    if (!b) {
      b = { id, from, to, columns: [], kinds: new Set(), count: 0, status: e.status || 'ACTIVE', polymorphicGroups: new Set(), manual: false };
      bundles.set(id, b);
    }
    (e.child.columns || []).forEach((c) => { if (!b.columns.includes(c)) b.columns.push(c); });
    b.kinds.add(e.kind || 'LOOKUP');
    b.count += 1;
    if (e.polymorphicGroup) b.polymorphicGroups.add(e.polymorphicGroup);
    if (e.kind === 'MANUAL' || e.origin === 'MANUAL') b.manual = true;
    if (e.status === 'REJECTED' && b.status !== 'REJECTED' && b.count === 1) b.status = 'REJECTED';
  });
  // A polymorphic lookup (Task.WhoId → Contact | Lead) is one field fanning out to several
  // parents: keep the lines, mark them as a group so they render lighter with one shared label.
  const groupMembers = new Map();
  bundles.forEach((b) => b.polymorphicGroups.forEach((g) => {
    if (!groupMembers.has(g)) groupMembers.set(g, []);
    groupMembers.get(g).push(b.id);
  }));
  bundles.forEach((b) => {
    const shared = [...b.polymorphicGroups].find((g) => groupMembers.get(g).length > 1);
    b.grouped = Boolean(shared);
    b.polymorphicGroup = shared || ([...b.polymorphicGroups][0] || null);
    b.groupLabel = shared ? shared.split('.').pop() : null;
  });
  // Fields connecting each neighbour to the centre, for tooltips and the neighbour table.
  bundles.forEach((b) => {
    if (b.from === centerKey && visual.has(b.to)) visual.get(b.to).fields.push(...b.columns.map((c) => `${centerNode.table}.${c}`));
    if (b.to === centerKey && visual.has(b.from)) visual.get(b.from).fields.push(...b.columns.map((c) => `${visual.get(b.from).table}.${c}`));
  });

  // ---- 5. x / y placement ---------------------------------------------------------------------
  let widest = centerNode.slot;
  layers.forEach((keys) => { widest = Math.max(widest, keys.reduce((s, k) => s + visual.get(k).slot, 0)); });
  const width = Math.max(o.minWidth, Math.ceil(widest + 2 * o.marginX));
  const yOf = (layer) => o.marginY + (layer + maxHops) * o.rowGap + o.rMax;
  const height = 2 * maxHops * o.rowGap + 2 * o.rMax + 2 * o.marginY + 14;
  centerNode.x = width / 2;
  centerNode.y = yOf(0);

  const neighbourKeys = (key, innerLayer) => {
    const out = [];
    bundles.forEach((b) => {
      const other = b.from === key ? b.to : (b.to === key ? b.from : null);
      if (other && visual.has(other) && visual.get(other).layer === innerLayer) out.push(other);
    });
    return out;
  };
  const place = (layer) => {
    const keys = layers.get(layer);
    if (!keys || keys.length === 0) return;
    const inner = layer < 0 ? layer + 1 : layer - 1;
    const bary = new Map();
    if (Math.abs(layer) >= 2) {
      keys.forEach((k) => {
        const ns = neighbourKeys(k, inner).map((n) => visual.get(n).x).filter((x) => x != null);
        bary.set(k, ns.length ? ns.reduce((s, x) => s + x, 0) / ns.length : Number.POSITIVE_INFINITY);
      });
    }
    const ordered = keys.slice().sort((a, b) => {
      const va = visual.get(a); const vb = visual.get(b);
      if (va.overflow !== vb.overflow) return va.overflow ? 1 : -1;
      if (bary.size) {
        const d = bary.get(a) - bary.get(b);
        if (Number.isFinite(d) && d !== 0) return d;
      }
      return byName(va, vb);
    });
    const layerWidth = ordered.reduce((s, k) => s + visual.get(k).slot, 0);
    let cum = (width - layerWidth) / 2;
    ordered.forEach((k) => {
      const v = visual.get(k);
      v.x = cum + v.slot / 2;
      v.y = yOf(layer);
      cum += v.slot;
    });
    layers.set(layer, ordered);
  };
  for (let d = 1; d <= maxHops; d += 1) { place(-d); place(d); }

  // ---- 6. edge routing ------------------------------------------------------------------------
  const outEdges = [];
  bundles.forEach((b) => {
    const c = visual.get(b.from);
    const p = visual.get(b.to);
    if (!c || !p || c.x == null || p.x == null) return;
    const dx = p.x - c.x; const dy = p.y - c.y;
    const len = Math.hypot(dx, dy);
    if (len === 0) return;
    const ux = dx / len; const uy = dy / len;
    const x1 = c.x + ux * (c.r + 1); const y1 = c.y + uy * (c.r + 1);
    const x2 = p.x - ux * (p.r + 4); const y2 = p.y - uy * (p.r + 4);
    const kinds = b.kinds;
    const kind = kinds.has('MASTER_DETAIL') ? 'MASTER_DETAIL'
      : (kinds.has('LOOKUP') || kinds.has('FK') || kinds.has('INFERRED')) ? 'LOOKUP'
        : (kinds.has('MANUAL') ? 'MANUAL' : [...kinds][0]);
    outEdges.push({
      id: b.id, from: b.from, to: b.to, x1, y1, x2, y2,
      lx: (x1 + x2) / 2 - uy * 8, ly: (y1 + y2) / 2 + ux * 8,
      kind, status: b.status, columns: b.columns, count: b.count,
      polymorphicGroup: b.polymorphicGroup, groupLabel: b.groupLabel, grouped: b.grouped,
      labelled: b.from === centerKey || b.to === centerKey, manual: b.manual,
    });
  });
  outEdges.sort((a, b) => a.id.localeCompare(b.id));

  const selfLoops = selfEdges.length === 0 ? [] : [{
    columns: [...new Set(selfEdges.flatMap((e) => e.child.columns || []))],
    kind: selfEdges.some((e) => e.kind === 'MASTER_DETAIL') ? 'MASTER_DETAIL' : (selfEdges[0].kind || 'LOOKUP'),
    count: selfEdges.length,
  }];

  const nodes = [...visual.values()]
    .filter((v) => v.x != null)
    .sort((a, b) => (a.layer - b.layer) || (a.x - b.x) || byName(a, b))
    .map(({ slot, ...v }) => v);

  return { width, height, nodes, edges: outEdges, selfLoops, omitted, maxRowCount };
}

export default layoutNetwork;
